import { createSelector } from '@reduxjs/toolkit'
import { parseISO, differenceInDays, isValid } from 'date-fns'
import { useModal } from '@afterpaytouch/core'
import {
  BiometricState,
  CheckoutAppSelector,
  useAppSelector,
  useInitialData,
  useIsOverduePaymentsFlow,
  useIsTrustedCheckout,
  useOAuthStateSelector,
  useConsumerTag,
  useConsumerSelector,
  useIsPersistentAndOptedIn,
  isCredentialPresent,
  isBiometricCapableBrowser,
  isEligibleBiometricLogin,
  useIsPrivateBrowsingMode,
  useAppDispatch,
  checkIsBiometricEnabledOnServer,
  useCLRiskDeclineError,
  useCohort,
  useIsBillingAgreementApprovalProcess,
  useHasBillingAgreements,
} from '..'
import { shouldAttemptAccountLink } from '../../utils/oAuth'
import {
  BIOMETRIC_DENIED_COUNT_KEY,
  BIOMETRIC_LAST_DENIED_DATE_KEY,
  BIOMETRIC_DENIED_DAYS,
  BIOMETRIC_MAX_DENIED_COUNT,
  BiometricsCheckTrigger,
  PaymentOptionsErrorModalKey,
} from '../../model'
import { Cohort } from '@afterpaytouch/portal-api'

/* Selectors */

export const biometricSelector: CheckoutAppSelector<BiometricState> = (state) => state.biometric

export const hasSilentErrorsSelector: CheckoutAppSelector<boolean> = createSelector(biometricSelector, (state) => state?.hasSilentErrors)

/* React Hooks */

export const useBiometric = (): BiometricState => useAppSelector(biometricSelector)

export const useHasSilentBiometricErrors = (): boolean => useAppSelector(hasSilentErrorsSelector)

export const useIsBiometricConflictingModal = (): boolean => {
  const showOverdueModal = useIsOverduePaymentsFlow()
  const { visible: showPaymentOptionsErrorModal } = useModal(PaymentOptionsErrorModalKey)
  const { isError: showCLRiskDeclineError } = useCLRiskDeclineError()

  return showOverdueModal || showPaymentOptionsErrorModal || showCLRiskDeclineError
}

export const useIsEligibleBiometricRegistration = (): boolean => {
  const { allowDisplayingModal } = useBiometric()
  const oAuthState = useOAuthStateSelector()
  const isBiometricCapableBrowser = useIsBiometricCapableBrowser()
  const isLoggedInWithOAuth = useIsTrustedCheckout()
  const isPersistentAndOptedIn = useIsPersistentAndOptedIn()
  const isPrivateBrowsingMode = useIsPrivateBrowsingMode()
  const hasBillingAgreements = useHasBillingAgreements()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  const { email, mobile, hasPassword, isUserVerifyAuthenticatorAvailable } = useConsumerSelector()
  const consumerTag = useConsumerTag()

  const isBiometricEnabled = isCredentialPresent({ email, mobile })

  const cohort = useCohort()

  if (cohort === Cohort.CashOnly || hasBillingAgreements || isBillingAgreementApprovalProcess) {
    return false
  }

  if (isUserVerifyAuthenticatorAvailable) {
    // checkout is launched with oAuth
    const hasOAuthParams = shouldAttemptAccountLink(oAuthState)

    const precondition =
      allowDisplayingModal &&
      isBiometricCapableBrowser &&
      !isBiometricEnabled &&
      !hasOAuthParams &&
      !isLoggedInWithOAuth &&
      hasPassword &&
      !isPersistentAndOptedIn &&
      !isPrivateBrowsingMode

    if (!precondition) {
      return false
    }

    // user has not declined 5 times -> check tag
    // it's been more than 7 days after the last time user denied -> check tag
    if (!(BIOMETRIC_DENIED_COUNT_KEY in consumerTag) || !(BIOMETRIC_LAST_DENIED_DATE_KEY in consumerTag)) {
      return precondition
    }

    const deniedCountValue = consumerTag[BIOMETRIC_DENIED_COUNT_KEY]
    const lastDeniedDateValue = consumerTag[BIOMETRIC_LAST_DENIED_DATE_KEY]

    if (deniedCountValue == null || lastDeniedDateValue == null) {
      return precondition
    }

    const deniedCount = parseInt(deniedCountValue, 10)
    const lastDate = parseISO(lastDeniedDateValue)

    if (Number.isNaN(deniedCount) || !isValid(lastDate)) {
      return precondition
    }

    const diffDays = differenceInDays(new Date(), lastDate)

    return deniedCount < BIOMETRIC_MAX_DENIED_COUNT && diffDays >= BIOMETRIC_DENIED_DAYS
  }

  return false
}

export const useIsEligibleBiometricLogin = (): boolean => {
  const oAuthState = useOAuthStateSelector()
  const { isUserVerifyAuthenticatorAvailable } = useConsumerSelector()
  const isBiometricCapableBrowser = useIsBiometricCapableBrowser()
  const isLoggedInWithOAuth = useIsTrustedCheckout()
  const isPrivateBrowsingMode = useIsPrivateBrowsingMode()
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly

  return isEligibleBiometricLogin({
    oAuthState,
    isBiometricCapableBrowser,
    isLoggedInWithOAuth,
    isUserVerifyAuthenticatorAvailable,
    isPrivateBrowsingMode,
    isCashOnlyCohort,
  })
}

export const useIsBiometricCapableBrowser = (): boolean => {
  const { isIframe } = useInitialData()

  return isBiometricCapableBrowser(isIframe)
}

// If true, device is eligible and user is registered for biometrics
// used in initialise and Login component to set isBiometricEnabledOnServer in redux
export const useBiometricsEligibleAndRegisteredCheckTrigger = (): BiometricsCheckTrigger => {
  const dispatch = useAppDispatch()

  return async (isBiometricsEligiblePayload, isMobileLogin, email, mobile) => {
    // The reason why we have 2 types for isBiometricsEligiblePayload is due to 2 things.
    // 1. Hooks vs actions. Initialise does not support hooks, so to check biometrics eligibility, a payload needs to be passed into a helper function
    // 2. If the user's account cannot be found during initialise, the user will be redirected to the Login (email) page.
    // If the account lookup is successful, we can use the useIsBiometricsEligible hook that returns a boolean, bypassing the isEligibleBiometricLogin helper.
    const isBiometricEligible =
      typeof isBiometricsEligiblePayload === 'boolean' ? isBiometricsEligiblePayload : isEligibleBiometricLogin(isBiometricsEligiblePayload)

    return isBiometricEligible && (await dispatch(checkIsBiometricEnabledOnServer(email, mobile, isMobileLogin)))
  }
}

// Autotrigger Biometrics button is different to this one
export const useShowBiometricsButton = (): boolean => {
  const { email, mobile } = useConsumerSelector()

  const { isBiometricEnabledOnServer = false } = useBiometric()
  const isBiometricEligible = useIsEligibleBiometricLogin()
  const hasSilentBiometricsErrors = useHasSilentBiometricErrors()

  return !hasSilentBiometricsErrors && !isCredentialPresent({ email, mobile }) && isBiometricEligible && isBiometricEnabledOnServer
}

// This should only be used on the LoginPassword component.
export const useShouldAutoTriggerBiometrics = (): boolean => {
  const isBiometricEligible = useIsEligibleBiometricLogin()
  const { email, mobile } = useConsumerSelector()
  const { isBiometricEnabledOnServer = false } = useBiometric()

  return isCredentialPresent({ email, mobile }) && isBiometricEligible && isBiometricEnabledOnServer
}
